import { BLSVisual } from '../BLSVisual';
import { SessionTimer } from './SessionTimer';
import type { Direction, MovementStyle, Shape } from '../../types';

interface BLSPreviewStripProps {
  color: string;
  direction: Direction;
  speed: number;
  movementStyle: MovementStyle;
  shape: Shape;
  background: string;
  /** Live BLS session flag — read-only (PREVIEW-02 boundary). */
  isPlaying: boolean;
  /** Drives status text + pill label. The strip never mutates connection state. */
  connectionMode: 'demo' | 'invite' | 'ready';
  /** True while the immersive (fullscreen) view is open. The strip stops animating locally to
   *  avoid the visible phase drift between the sidebar and immersive AnimationEngine
   *  singletons (separate React instances → separate timers). */
  isImmersiveOpen: boolean;
  /** Optional pan-update callback. The strip forwards it to BLSVisual so the parent's
   *  audio engine can trigger bilateral beeps at the visual's pan extremes. The strip
   *  itself never touches audio (PREVIEW-02 boundary). */
  onPanUpdate?: (pan: number) => void;
  onExpandToFullscreen: () => void;
}

/**
 * BLSPreviewStrip — Permanent BLS preview pinned just above the sticky footer.
 *
 * The strip is now display-only — the play/stop control lives in the footer where it can
 * be re-labeled per connection mode ("Play BLS preview" in demo, "▶ START" in ready).
 * Only interactive element here is the expand-to-fullscreen icon.
 *
 * Status pill semantics:
 *   READY + isPlaying  → red "● Live" (real broadcast in flight)
 *   DEMO/INVITE + isPlaying → neutral "● Preview" (local only; nothing being broadcast)
 *
 * A small status text in the bottom-left of the strip is updated in a polite live region so
 * SR users get orientation without firing alerts.
 *
 * Boundary: NO sendMessage / sendToOtherInstances / BLSContext / zoomAPI imports.
 */
export function BLSPreviewStrip({
  color,
  direction,
  speed,
  movementStyle,
  shape,
  background,
  isPlaying,
  connectionMode,
  isImmersiveOpen,
  onPanUpdate,
  onExpandToFullscreen,
}: BLSPreviewStripProps): JSX.Element {
  // Fixed 140px regardless of connection state. Earlier iterations tried a compact 80/110px
  // mode for READY/INVITE, but the smaller heights cramped the ball and the differentiation
  // didn't justify the layout shift.
  const heightClass = 'h-[140px]';
  const isLiveBroadcast = isPlaying && connectionMode === 'ready';
  const isLocalPreview = isPlaying && connectionMode !== 'ready';

  const statusText = isImmersiveOpen
    ? 'Playing in fullscreen. Animation moved to immersive view.'
    : isLiveBroadcast
      ? 'Live — broadcasting to patient.'
      : isLocalPreview
        ? 'Demo preview playing — not broadcasting.'
        : connectionMode === 'demo'
          ? 'Demo preview — paused.'
          : connectionMode === 'invite'
            ? 'Preview — paused. Waiting for patient to add the app.'
            : 'Preview — paused.';

  return (
    <section
      aria-label="BLS preview"
      className={`relative w-full ${heightClass} bg-app-bg border-t border-gray-200 flex-shrink-0 overflow-hidden`}
    >
      {/* Visual surface — decorative, hidden from AT. While immersive is open, the strip
          shows a placeholder instead of BLSVisual so the two AnimationEngine singletons
          don't visibly drift out of phase. */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={background ? { backgroundColor: background } : undefined}
        aria-hidden="true"
      >
        {isImmersiveOpen ? (
          <p className="text-sm font-medium text-white px-3 py-1.5 rounded-md bg-emdr-navy/95 backdrop-blur-sm">
            Playing in fullscreen
          </p>
        ) : (
          <BLSVisual
            color={color}
            direction={direction}
            speed={speed}
            isPlaying={isPlaying}
            movementStyle={movementStyle}
            shape={shape}
            onPanUpdate={onPanUpdate}
            small
          />
        )}
      </div>

      {/* Status pill — labels carry the meaning, not color alone (WCAG 1.4.1). */}
      {isImmersiveOpen ? (
        <span
          aria-hidden="true"
          className="absolute top-2 left-2 z-10 bg-emdr-navy/95 text-white text-xs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm"
        >
          ● Playing in fullscreen
        </span>
      ) : isLiveBroadcast ? (
        <span
          aria-hidden="true"
          className="absolute top-2 left-2 z-10 bg-red-600/90 text-white text-xs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm"
        >
          ● Live
        </span>
      ) : isLocalPreview ? (
        <span
          aria-hidden="true"
          className="absolute top-2 left-2 z-10 bg-emdr-navy/90 text-white text-xs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm"
        >
          ● Preview
        </span>
      ) : null}

      {/* Session timer — top-right, only while playing. aria-hidden so SR isn't pelted by
          the 1Hz tick; meaningful start/stop is already announced by the session-state
          region. Solid bg-white keeps text readable over any user-selectable background. */}
      {isPlaying && (
        <div
          aria-hidden="true"
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-white text-gray-900 px-2 py-0.5 rounded-md shadow-sm"
        >
          <span className="text-xs">⏱</span>
          <SessionTimer isPlaying={isPlaying} className="font-mono text-xs font-medium tabular-nums" />
        </div>
      )}

      {/* Status text — polite live region. Updates whenever statusText flips. */}
      <p
        role="status"
        aria-live="polite"
        className="absolute bottom-2 left-2 z-10 text-xs text-emdr-navy bg-white/85 backdrop-blur-sm px-2 py-1 rounded max-w-[calc(100%-60px)] truncate"
      >
        {statusText}
      </p>

      {/* Expand / collapse to fullscreen — 44×44 with dual-tone focus ring.
          aria-expanded conveys the immersive open/closed state pairing with the label swap. */}
      <button
        type="button"
        aria-label={isImmersiveOpen ? 'Collapse preview from fullscreen' : 'Expand preview to fullscreen'}
        aria-expanded={isImmersiveOpen}
        onClick={onExpandToFullscreen}
        className="absolute bottom-2 right-2 z-10 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md bg-white/85 hover:bg-white text-emdr-navy backdrop-blur-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emdr-navy focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        {isImmersiveOpen ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {/* "collapse" — arrows pointing inward */}
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V5m0 4H5m0-4l4 4m11-4v4m0 0h-4m4 0l-4-4M9 15v4m0-4H5m0 0l4 4m11-4h-4m4 0v4m0-4l-4 4" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {/* "expand" — arrows pointing outward */}
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        )}
      </button>
    </section>
  );
}
